import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 30000 // 30 seconds timeout
});

// Add token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authApi = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    me: () => api.get('/auth/me')
};

// Restaurants
export const restaurantsApi = {
    getAll: () => api.get('/restaurants')
};

// Budget
export const budgetApi = {
    getDashboard: (restaurantName, date) => 
        api.get(`/budget/dashboard/${encodeURIComponent(restaurantName)}?date_str=${date}`),
    getDetailed: (restaurantName, date) => 
        api.get(`/budget/detailed/${encodeURIComponent(restaurantName)}?date_str=${date}`),
    getComparison: (restaurantName, date) => 
        api.get(`/budget/comparison/${encodeURIComponent(restaurantName)}?date_str=${date}`),
    getConsolidated: (date) => 
        api.get(`/budget/consolidated?date_str=${date}`),
    getMonthlyComparison: (restaurantName, date) =>
        api.get(`/budget/monthly-comparison/${encodeURIComponent(restaurantName)}?date_str=${date}`)
};

// Admin
export const adminApi = {
    getUsers: () => api.get('/admin/users'),
    createUser: (data) => api.post('/admin/users', data),
    updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    importData: () => api.post('/admin/import-data'),
    syncGoogleSheet: () => api.post('/admin/sync-google-sheet'),
    syncTrivec: () => api.post('/admin/sync-trivec'),
    getTrivecStatus: () => api.get('/admin/trivec-status'),
    initUsers: () => api.post('/admin/init-users')
};

// Effectifs (Pilotage des effectifs)
export const effectifsApi = {
    getWeekly: (restaurantName, weekStart) => 
        api.get(`/effectifs/weekly/${encodeURIComponent(restaurantName)}?week_start=${weekStart}`),
    getBudget: (restaurantName, month) => 
        api.get(`/effectifs/budget/${encodeURIComponent(restaurantName)}?month=${encodeURIComponent(month)}`),
    getComparison: (restaurantName, weekStart) => 
        api.get(`/effectifs/comparison/${encodeURIComponent(restaurantName)}?week_start=${weekStart}`),
    getAllRestaurants: (weekStart) => 
        api.get(`/effectifs/all-restaurants?week_start=${weekStart}`),
    getAllRestaurantsMonthly: (month, year) => 
        api.get(`/effectifs/all-restaurants/monthly?month=${month}&year=${year}`),
    getYtd: (restaurantName, weekStart) => 
        api.get(`/effectifs/ytd/${encodeURIComponent(restaurantName)}?week_start=${weekStart}`),
    getYtdChart: (year, restaurantName = null) => {
        let url = `/effectifs/ytd-chart?year=${year}`;
        if (restaurantName) {
            url += `&restaurant_name=${encodeURIComponent(restaurantName)}`;
        }
        return api.get(url);
    },
    syncMonthlyCache: (year, month = null) => {
        let url = `/effectifs/sync-monthly-cache?year=${year}`;
        if (month) {
            url += `&month=${month}`;
        }
        return api.post(url);
    },
    getCacheStatus: (year) => 
        api.get(`/effectifs/cache-status?year=${year}`),
    getStaffByService: (restaurantName, weekStart) =>
        api.get(`/effectifs/staff-by-service/${encodeURIComponent(restaurantName)}?week_start=${weekStart}`),
    getStaffByServiceConsolidated: (weekStart) =>
        api.get(`/effectifs/staff-by-service-consolidated?week_start=${weekStart}`),
    getResponsibilityPresence: (restaurantName, weekStart) =>
        api.get(`/effectifs/responsibility-presence/${encodeURIComponent(restaurantName)}?week_start=${weekStart}`),
    getResponsibilityPresenceConsolidated: (weekStart) =>
        api.get(`/effectifs/responsibility-presence-consolidated?week_start=${weekStart}`)
};

// Trivec
export const trivecApi = {
    getTicketMoyen: (restaurantName, date) => 
        api.get(`/trivec/ticket-moyen/${encodeURIComponent(restaurantName)}?date_str=${date}`),
    getVentesParUser: (restaurantName, date) => 
        api.get(`/trivec/ventes-par-user/${encodeURIComponent(restaurantName)}?date_str=${date}`)
};

// POS (Point of Sale) - Tables ouvertes
export const posApi = {
    getOpenTables: (restaurantName) => 
        api.get(`/pos/open-tables/${encodeURIComponent(restaurantName)}`)
};

// Reservations (Zenchef)
export const reservationsApi = {
    getSummary: (date) => api.get(`/reservations/summary?date_str=${date}`),
    getVip: (date) => api.get(`/reservations/vip?date_str=${date}`)
};

// Marketing (Myli)
export const myliApi = {
    getGroups: () => api.get('/myli/groups'),
    getGroupLocations: (groupId) => api.get(`/myli/groups/${groupId}/locations`),
    getLocation: (locationId) => api.get(`/myli/locations/${locationId}`),
    getLocationReviews: (locationId, params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        return api.get(`/myli/locations/${locationId}/reviews${queryParams ? '?' + queryParams : ''}`);
    },
    getGroupReviews: (groupId, params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        return api.get(`/myli/groups/${groupId}/reviews${queryParams ? '?' + queryParams : ''}`);
    },
    getDashboard: (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        return api.get(`/myli/dashboard${queryParams ? '?' + queryParams : ''}`);
    }
};

// Briefing du jour
export const briefingApi = {
    getBriefing: (restaurantName, date) =>
        api.get(`/briefing/${encodeURIComponent(restaurantName)}?date_str=${date}`),
    getConsolidated: (date) =>
        api.get(`/briefing-consolidated?date_str=${date}`)
};

// Compte d'exploitation (SIG)
export const exploitationApi = {
    getData: (restaurantName, month, year = 2026) => 
        api.get(`/exploitation/${encodeURIComponent(restaurantName)}?month=${month}&year=${year}`),
    getConsolidated: (month, year = 2026, restaurants = null) => 
        api.get(`/exploitation/consolidated?month=${month}&year=${year}${restaurants?.length ? `&restaurants=${encodeURIComponent(restaurants.join(','))}` : ''}`),
    getLanding: (restaurantName, month, year = 2026) =>
        api.get(`/exploitation/${encodeURIComponent(restaurantName)}/landing?month=${month}&year=${year}`),
    getLandingConsolidated: (month, year = 2026, restaurants = null) =>
        api.get(`/exploitation/landing/consolidated?month=${month}&year=${year}${restaurants?.length ? `&restaurants=${encodeURIComponent(restaurants.join(','))}` : ''}`),
    getStructure: () => api.get('/exploitation/structure')
};

// KPI (Ratios)
export const kpiApi = {
    getRatios: (restaurantName, month, year = 2026) =>
        api.get(`/kpi/ratios/${encodeURIComponent(restaurantName)}?month=${month}&year=${year}`),
    getRatiosConsolidated: (month, year = 2026) =>
        api.get(`/kpi/ratios-consolidated?month=${month}&year=${year}`)
};

// Seed
export const seedApi = {
    seed: () => api.post('/seed')
};

export default api;
